/**
 * Pure helpers (NO imports) so they're unit-testable without pulling in
 * `@rails/actioncable` or the Redux store. Builds the ActionCable socket URL.
 *
 * Why this exists: the **desktop (Tauri) app** loads the SPA from the app
 * bundle, so the document origin is `tauri://localhost` (macOS/Linux) or
 * `http://tauri.localhost` (Windows). Neither hosts the `/cable` endpoint, so
 * the old `window.location.origin + '/cable'` produced `tauri://localhost/cable`
 * (the `http→ws` replace didn't even match) and the CRM realtime socket could
 * never connect — campaign-status + CSV-import progress silently never updated
 * on desktop. We must instead target the real dashboard host (`apiBase`, i.e.
 * `env.apiUrl`), where `vite preview` proxies the ws upgrade to crm-api.
 *
 * Why NOT just "use apiBase when set": in a browser the SPA is same-origin
 * with `/cable`, so `window.location.origin` is correct — and in LOCAL DEV
 * `env.apiUrl` points at `:4000` (Fastify, which has no `/cable`), so using it
 * for the socket would break dev. Hence we switch on the Tauri runtime
 * specifically, leaving every browser path (prod + dev) untouched.
 */
export interface CableLoc {
  origin: string;
  protocol: string;
  hostname: string;
}

/** True only inside the Tauri desktop webview (both OS origin shapes). */
export function isDesktopRuntime(loc: CableLoc): boolean {
  return loc.protocol === "tauri:" || loc.hostname === "tauri.localhost";
}

/**
 * Resolve the `ws(s)://…/cable` URL.
 * - Desktop (Tauri): use `apiBase` (the dashboard host) when provided.
 * - Browser (prod + dev): use the same-origin `loc.origin`.
 * Falls back to `loc.origin` if desktop but `apiBase` is empty (no crash).
 */
export function resolveCableUrl(loc: CableLoc, apiBase: string): string {
  const base = isDesktopRuntime(loc) && apiBase ? apiBase : loc.origin;
  return (
    base
      .replace(/^https:/, "wss:")
      .replace(/^http:/, "ws:")
      .replace(/\/+$/, "") + "/cable"
  );
}
